/**
 * Apply one pax-recalc event, and the mid-flight crash / restart events.
 *
 * The numbers are computed in Rust from the fleet book. This module only
 * writes them onto the mission, the cost ledger, and the weight sheet
 * together. It does not recompute weights from a second copy of the tail.
 */
import { flightMinutesForDuty, reverseFlightDuty, type DutyRoster } from "./duty";
import { flightCostLineId } from "./flightCostLedger";
import { hoursFromTrip } from "./maintenance";
import type { CostLineItem, CrewLedgerEntry, OpsMoneyCode } from "./opsLedger";
import { careerBeforeFromTail, stripFlightTelemetry, type TailCareerBefore, type TailTelemetryState } from "./simTelemetry";
import {
  computeWeightBalance,
  envelopeLimitsAtWeight,
  type WbProfile,
  type WbSeat,
  type WbStation,
  type WbTank,
  type WeightBalanceDesk,
} from "./weightBalance";
import type { AircraftSpec, CompanyAccount, FlightLogEntry, Mission, PilotLogbookEntry, Squawk } from "./types";
import { wholeFuelLb } from "./wholeFuel";

export const PAX_RECALC_COMMAND = "pax_recalc";
export const FLIGHT_CRASH_COMMAND = "flight_crash";
export const FLIGHT_RESTART_COMMAND = "flight_restart";
export const PAX_RECALC_EVENT = "pax://recalculated";
export const FLIGHT_CRASH_EVENT = "flight://crashed";
export const FLIGHT_RESTART_EVENT = "flight://restarted";

export type PaxLimitFlag = {
  code: "MTOW" | "MLW" | "MZFW" | "FUEL" | string;
  limitLb: number;
  actualLb: number;
  message: string;
};

export type PaxMoneyLine = {
  id: string;
  seq: number;
  amount: number;
  memo: string;
  category: string;
  currency: string;
};

export type PaxSectorOut = {
  seq: number;
  origin: string;
  dest: string;
  paxCount: number;
  fuelGal: number;
  fuelLb: number;
  tripBurnLb: number;
  taxiFuelLb: number;
  reserveFuelLb: number;
  zfwLb: number;
  rampLb: number;
  towLb: number;
  landingLb: number;
  flags: PaxLimitFlag[];
};

export type PaxTechStop = {
  fuelLb: number;
  fuelGal: number;
  secondFuelLb: number;
  secondFuelGal: number;
  whereIcao: string;
  whereNote: string;
  whenIso: string;
};

/** Payload of `pax://recalculated`. Aircraft limits were read from the fleet book. */
export type PaxRecalcEvent = {
  missionId: string;
  aircraftId: string;
  registration: string;
  paxCount: number;
  cargoLb: number;
  includedBagLb: number;
  extraCargoLb: number;
  paxBodyLb: number;
  paxWeightUnset: boolean;
  fuelGal: number;
  fuelLb: number;
  tripBurnLb: number;
  taxiFuelLb: number;
  oewLb: number;
  mzfwLb: number;
  mlwLb: number;
  mtowLb: number;
  maxFuelLb: number;
  zfwLb: number;
  rampLb: number;
  towLb: number;
  landingLb: number;
  flags: PaxLimitFlag[];
  overridden: boolean;
  loadWarning: string;
  cateringLines: PaxMoneyLine[];
  fuelLines: PaxMoneyLine[];
  techStop: PaxTechStop | null;
  baggageRefused: boolean;
  cateringDropped: boolean;
  fuelRoundedToFit: boolean;
  sectors: PaxSectorOut[];
};

export type FlightCrashEvent = {
  missionId: string;
  treatAsNeverHappened: boolean;
  voidCosts: boolean;
  voidHours: boolean;
  keepCrewPay: boolean;
  choices: string[];
};

export type FlightRestartEvent = {
  missionId: string;
  anchorIso: string;
  times: { key: string; iso: string }[];
};

export type PaxRecalcSectorArg = {
  seq: number;
  eteMin: number;
  origin: string;
  dest: string;
  departureIso: string;
  distanceNm: number;
  paxCount: number;
  cateringLineId: string;
  fuelLineId: string;
};

export type PaxRecalcArgs = {
  missionId: string;
  aircraftId: string;
  paxCount: number;
  cargoLb: number;
  standardPaxLb: number;
  flightType: string;
  cateringRate: number;
  fuelRate: number;
  companyFuelUnit: string;
  currency: string;
  overrideLimits: boolean;
  flightNumber: string;
  sectors: PaxRecalcSectorArg[];
  lines: PaxMoneyLine[];
  voidedIds: string[];
  candidates: { icao: string; eteMin: number; distanceNm: number }[];
};

type Runner = {
  recalc: (args: PaxRecalcArgs) => Promise<PaxRecalcEvent>;
  crash: (args: { missionId: string; airborne: boolean }) => Promise<FlightCrashEvent>;
  restart: (args: { missionId: string; nowMs: number; times: { key: string; iso: string }[] }) => Promise<FlightRestartEvent>;
};

let runner: Runner | null = null;

export function setPaxLoadRunner(next: Runner | null): void {
  runner = next;
}

export function getPaxLoadRunner(): Runner | null {
  return runner;
}

export function missionIsAirborne(mission: Pick<Mission, "status" | "lifecycle">): boolean {
  return mission.lifecycle === "airborne" || mission.lifecycle === "diverting" || mission.status === "in-progress";
}

function moneyCode(raw: string): OpsMoneyCode {
  if (raw === "USD" || raw === "EUR" || raw === "GBP" || raw === "CAD") return raw;
  return "CAD";
}

function replaceMarked(existing: string | undefined, marker: string, next: string): string | undefined {
  const kept = String(existing ?? "")
    .split(" · ")
    .map((part) => part.trim())
    .filter((part) => part && !part.startsWith(marker));
  if (next.trim()) kept.push(next.trim());
  return kept.length ? kept.join(" · ") : undefined;
}

function share(index: number, count: number, total: number): number {
  if (count <= 0) return 0;
  const base = Math.floor((total / count) * 100) / 100;
  if (index === count - 1) return Math.round((total - base * (count - 1)) * 100) / 100;
  return base;
}

export function buildPaxRecalcArgs(input: {
  mission: Mission;
  paxCount: number;
  cargoLb: number;
  overrideLimits: boolean;
  standardPaxLb: number;
  cateringRate: number;
  fuelRate: number;
  companyFuelUnit: string;
  currency: string;
  lines: readonly CostLineItem[];
  voidedIds: readonly string[];
}): PaxRecalcArgs {
  const mission = input.mission;
  const legs = mission.legs?.length
    ? mission.legs
    : [
        {
          seq: 1,
          originIcao: mission.originIcao,
          destIcao: mission.destIcao,
          distanceNm: mission.distanceNm,
          eteMin: mission.eteMin,
          departureIso: mission.departureIso,
          paxCount: mission.paxCount,
        },
      ];
  const sectors = legs.map((leg, index) => {
    const seq = Number.isFinite(leg.seq) && leg.seq > 0 ? Math.floor(leg.seq) : index + 1;
    return {
      seq,
      eteMin: leg.eteMin,
      origin: leg.originIcao,
      dest: leg.destIcao,
      departureIso: "departureIso" in leg && leg.departureIso ? leg.departureIso : mission.departureIso,
      distanceNm: leg.distanceNm,
      paxCount: leg.paxCount,
      cateringLineId: flightCostLineId(mission.id, "catering", seq),
      fuelLineId: flightCostLineId(mission.id, "fuel", seq),
    };
  });
  const lines = input.lines
    .filter((line) => line.missionId === mission.id && (line.category === "catering" || line.category === "fuel"))
    .map((line) => ({
      id: line.id,
      seq: 0,
      amount: line.amount,
      memo: line.memo ?? "",
      category: line.category,
      currency: line.currency,
    }));
  return {
    missionId: mission.id,
    aircraftId: mission.aircraftId,
    paxCount: input.paxCount,
    cargoLb: input.cargoLb,
    standardPaxLb: input.standardPaxLb,
    flightType: mission.flightType,
    cateringRate: input.cateringRate,
    fuelRate: input.fuelRate,
    companyFuelUnit: input.companyFuelUnit,
    currency: input.currency,
    overrideLimits: input.overrideLimits,
    flightNumber: mission.flightNumber || mission.callsign || "",
    sectors,
    lines,
    voidedIds: [...input.voidedIds],
    candidates: [],
  };
}

function mergeMoney(ledger: readonly CostLineItem[], eventLines: readonly PaxMoneyLine[], mission: Mission, nowIso: string): CostLineItem[] {
  const next = ledger.map((line) => ({ ...line }));
  for (const row of eventLines) {
    if (!row.id) continue;
    const hit = next.find((line) => line.id === row.id);
    if (hit) {
      hit.amount = row.amount;
      hit.memo = row.memo;
      continue;
    }
    next.unshift({
      id: row.id,
      category: row.category === "fuel" ? "fuel" : "catering",
      amount: row.amount,
      currency: moneyCode(row.currency),
      createdAtUtc: nowIso,
      missionId: mission.id,
      ...(mission.flightNumber ? { flightNumber: mission.flightNumber } : {}),
      ...(mission.aircraftId ? { aircraftId: mission.aircraftId } : {}),
      ...(row.memo ? { memo: row.memo } : {}),
    });
  }
  return next;
}

function seatPax(seats: readonly WbSeat[], order: readonly number[], paxCount: number, paxBodyLb: number): WbSeat[] {
  const pax = Math.max(0, paxCount);
  const taken = order.slice(0, Math.min(pax, seats.length));
  const out = seats.map((seat) => ({ ...seat, occupied: false }));
  taken.forEach((seatIndex, i) => {
    const row = out[seatIndex];
    if (row) out[seatIndex] = { ...row, occupied: true, weightLb: share(i, taken.length, paxBodyLb) };
  });
  return out;
}

function stowCargo(cargo: readonly WbStation[], plan: "aft" | "fwd" | "split", cargoLb: number): WbStation[] {
  if (!cargo.length) return [];
  const arms = cargo.map((row) => row.armIn);
  const target = plan === "aft" ? arms.indexOf(Math.max(...arms)) : arms.indexOf(Math.min(...arms));
  return cargo.map((row, index, all) => ({
    ...row,
    weightLb: plan === "split" ? share(index, all.length, cargoLb) : index === target ? cargoLb : 0,
  }));
}

const AUX_TANK = /ventral|aux|tip|ferry/i;

/** Wing / main tanks first, evenly up to capacity; auxiliary (ventral) tanks only take what the mains cannot hold. */
export function fillWbTanks(tanks: readonly WbTank[], fuelLb: number): WbTank[] {
  const out = tanks.map((tank) => ({ ...tank, quantityLb: 0 }));
  let left = Math.max(0, fuelLb);
  const groups = [
    out.filter((tank) => !AUX_TANK.test(`${tank.id} ${tank.label}`)),
    out.filter((tank) => AUX_TANK.test(`${tank.id} ${tank.label}`)),
  ];
  for (const group of groups) {
    let open = group.filter((tank) => tank.capacityLb > 0);
    while (left > 0.005 && open.length) {
      const each = left / open.length;
      const full = open.filter((tank) => tank.capacityLb - tank.quantityLb <= each);
      if (!full.length) {
        const pour = left;
        open.forEach((tank, index) => {
          tank.quantityLb = Math.round((tank.quantityLb + share(index, open.length, pour)) * 100) / 100;
        });
        left = 0;
        break;
      }
      for (const tank of full) {
        left -= tank.capacityLb - tank.quantityLb;
        tank.quantityLb = tank.capacityLb;
      }
      open = open.filter((tank) => !full.includes(tank));
    }
  }
  const last = out[out.length - 1];
  if (left > 0.005 && last) {
    last.quantityLb = Math.round((last.quantityLb + left) * 100) / 100;
    last.capacityLb = Math.max(last.capacityLb, last.quantityLb);
  }
  return out;
}

function cgMarginPct(profile: WbProfile): number {
  const result = computeWeightBalance(profile);
  let margin = Infinity;
  for (const point of [result.zfw, result.tow, result.ldg]) {
    const limits = envelopeLimitsAtWeight(profile.envelope, point.weightLb);
    if (!limits || !Number.isFinite(limits.fwdPctMac) || !Number.isFinite(limits.aftPctMac)) return -Infinity;
    margin = Math.min(margin, point.pctMac - limits.fwdPctMac, limits.aftPctMac - point.pctMac);
  }
  return margin;
}

/**
 * Lay the load on the sheet: fuel fills the mains before the ventral, then the
 * seating and baggage plan with the widest CG margin wins (aft seats first,
 * bags aft is tried first and wins ties).
 */
function applySheet(profile: WbProfile, event: PaxRecalcEvent): WbProfile {
  const base: WbProfile = {
    ...profile,
    tripFuelLb: event.tripBurnLb,
    tanks: fillWbTanks(profile.tanks, event.fuelLb),
    ...(event.loadWarning ? { loadFlagNote: event.loadWarning } : { loadFlagNote: undefined }),
    limits: {
      ...profile.limits,
      bowLb: event.oewLb,
      mzfwLb: event.mzfwLb,
      mlwLb: event.mlwLb,
      mtowLb: event.mtowLb,
      taxiFuelLb: event.taxiFuelLb,
      maxFuelLb: event.maxFuelLb > 0 ? event.maxFuelLb : profile.limits.maxFuelLb,
    },
  };
  const byArm = profile.seats.map((seat, index) => ({ index, armIn: seat.armIn }));
  const aftFirst = [...byArm].sort((a, b) => b.armIn - a.armIn || a.index - b.index).map((row) => row.index);
  const frontFirst = [...byArm].sort((a, b) => a.armIn - b.armIn || a.index - b.index).map((row) => row.index);
  const spread = frontFirst.filter((_, i) => i % 2 === 0).concat(aftFirst.filter((_, i) => i % 2 === 0)).filter((v, i, all) => all.indexOf(v) === i);
  const spreadOrder = spread.concat(frontFirst.filter((index) => !spread.includes(index)));
  const cargoLb = event.includedBagLb + event.extraCargoLb;
  let best: WbProfile | null = null;
  let bestMargin = -Infinity;
  for (const order of [aftFirst, spreadOrder, frontFirst]) {
    for (const plan of ["aft", "split", "fwd"] as const) {
      const candidate: WbProfile = {
        ...base,
        seats: seatPax(profile.seats, order, event.paxCount, event.paxBodyLb),
        cargo: stowCargo(profile.cargo, plan, cargoLb),
      };
      const margin = cgMarginPct(candidate);
      if (!best || margin > bestMargin + 1e-9) {
        best = candidate;
        bestMargin = margin;
      }
    }
  }
  return best ?? base;
}

export type PaxRecalcDesk = {
  mission: Mission;
  ledger: CostLineItem[];
  profile: WbProfile;
  nowIso?: string;
};

/** One event updates the ticket, the catering and fuel lines, and the weight sheet. */
export function applyPaxRecalcEvent(desk: PaxRecalcDesk, event: PaxRecalcEvent): {
  mission: Mission;
  ledger: CostLineItem[];
  profile: WbProfile;
} {
  if (event.baggageRefused || event.cateringDropped || event.fuelRoundedToFit) {
    return { mission: desk.mission, ledger: [...desk.ledger], profile: desk.profile };
  }
  const mission = desk.mission;
  const names = Array.isArray(mission.paxNames) ? mission.paxNames.slice(0, event.paxCount) : mission.paxNames;
  const legs = mission.legs?.length
    ? mission.legs.map((leg) => {
        const sector = event.sectors.find((row) => row.seq === leg.seq);
        if (!sector) return leg;
        return {
          ...leg,
          paxCount: sector.paxCount,
          paxNames: leg.seq === mission.legs[0]?.seq ? names : leg.paxNames.slice(0, sector.paxCount),
          fuelGal: sector.fuelGal,
          fuelLbs: wholeFuelLb(sector.fuelLb),
        };
      })
    : mission.legs;
  const flags = event.flags
    .map((flag) => flag.code)
    .filter((code): code is NonNullable<Mission["loadFlags"]>[number] => code === "MTOW" || code === "MLW" || code === "MZFW" || code === "FUEL");
  const nextMission: Mission = {
    ...mission,
    paxCount: event.paxCount,
    paxNames: names,
    cargoLb: event.cargoLb,
    fuelGal: event.fuelGal,
    fuelLbs: wholeFuelLb(event.fuelLb),
    legs,
    loadFlags: flags,
    loadWarning: event.loadWarning || undefined,
    weightOverride: event.overridden,
    techStopSuggestion: event.techStop
      ? {
          fuelLb: wholeFuelLb(event.techStop.fuelLb),
          fuelGal: event.techStop.fuelGal,
          secondFuelLb: wholeFuelLb(event.techStop.secondFuelLb),
          secondFuelGal: event.techStop.secondFuelGal,
          whereIcao: event.techStop.whereIcao,
          whereNote: event.techStop.whereNote,
          whenIso: event.techStop.whenIso,
        }
      : undefined,
    dispatchWarning: replaceMarked(mission.dispatchWarning, "WEIGHT", event.loadWarning),
  };
  const cateringBefore = desk.ledger.filter((line) => line.missionId === mission.id && line.category === "catering");
  let ledger = mergeMoney(desk.ledger, event.cateringLines, mission, desk.nowIso ?? new Date().toISOString());
  ledger = mergeMoney(ledger, event.fuelLines, mission, desk.nowIso ?? new Date().toISOString());
  const cateringAfter = ledger.filter((line) => line.missionId === mission.id && line.category === "catering");
  if (cateringAfter.length < cateringBefore.length) {
    return { mission: desk.mission, ledger: [...desk.ledger], profile: desk.profile };
  }
  return {
    mission: nextMission,
    ledger,
    profile: applySheet(desk.profile, event),
  };
}

export function applySheetToDesk(desk: WeightBalanceDesk, aircraftId: string, profile: WbProfile): WeightBalanceDesk {
  return {
    selectedAircraftId: aircraftId || desk.selectedAircraftId,
    profiles: { ...desk.profiles, [profile.aircraftId || aircraftId]: profile },
  };
}

function reverseHours(spec: AircraftSpec, posted: NonNullable<Mission["postedHours"]>): AircraftSpec {
  const engineHoursEach =
    spec.engineHoursEach && posted.engineEach
      ? spec.engineHoursEach.map((hours, index) =>
          Math.max(0, Math.round((hours - (posted.engineEach?.[index] ?? 0)) * 10) / 10),
        )
      : spec.engineHoursEach;
  return {
    ...spec,
    airframeHours: Math.max(0, Math.round((spec.airframeHours - posted.airframe) * 100) / 100),
    engineHours: Math.max(0, Math.round(((spec.engineHours ?? spec.airframeHours) - posted.engine) * 100) / 100),
    apuHours: Math.max(0, Math.round(((spec.apuHours ?? 0) - posted.apu) * 100) / 100),
    cycles: posted.cycle ? Math.max(0, (spec.cycles ?? 0) - 1) : spec.cycles,
    engineHoursEach,
  };
}

function restoreTail(spec: AircraftSpec, snap: TailCareerBefore): AircraftSpec {
  return {
    ...spec,
    fuelOnBoardGal: snap.fuelOnBoardGal,
    airframeHours: snap.airframeHours,
    engineHours: snap.engineHours,
    apuHours: snap.apuHours,
    engineHoursEach: snap.engineHoursEach ? [...snap.engineHoursEach] : undefined,
    cycles: snap.cycles,
    lastAirportIcao: snap.lastAirportIcao,
    status: snap.status,
    available: snap.available,
  };
}

function basisFromMission(mission: Mission): TailCareerBefore | undefined {
  const basis = mission.crashBasis;
  if (!basis || typeof basis.airframeHours !== "number" || !Number.isFinite(basis.airframeHours)) return undefined;
  if (basis.status !== "available" && basis.status !== "grounded" && basis.status !== "ferry" && basis.status !== "in_mx") {
    return undefined;
  }
  if (typeof basis.available !== "boolean") return undefined;
  return basis;
}

function careerSnapshot(
  desk: CrashDesk,
  mission: Mission,
  log?: FlightLogEntry,
): TailCareerBefore | undefined {
  const saved = basisFromMission(mission);
  if (saved) return saved;
  const tel = desk.tailTelemetry;
  if (!tel) return undefined;
  const owns = (row: { tripId?: string; flightLogId?: string; careerBefore?: TailCareerBefore }) =>
    (row.tripId === mission.id || (log && row.flightLogId === log.id)) && row.careerBefore;
  if (tel.session && owns(tel.session)) return tel.session.careerBefore;
  return tel.trips.find((trip) => owns(trip))?.careerBefore;
}

export type CrashDesk = {
  missions: Mission[];
  costLedger: CostLineItem[];
  account: CompanyAccount;
  fleet: AircraftSpec[];
  flightLogs: FlightLogEntry[];
  logbook: PilotLogbookEntry[];
  crewLedger: CrewLedgerEntry[];
  duty?: DutyRoster;
  tailTelemetry?: TailTelemetryState;
  squawks?: Squawk[];
};

const CRASH_NOTE = "CRASH  mid-flight — fuel, hours, money, and crew pay come off. Delete the RFQ or restart.";

function tailMatches(spec: AircraftSpec, mission: Mission): boolean {
  return spec.id === mission.aircraftId || spec.registration === mission.registration;
}

/** The flight leaves no ledger row, no hours, and no costs. Confirming the sim-crash prompt includes crew pay. */
export function applyFlightCrash(desk: CrashDesk, event: FlightCrashEvent): CrashDesk {
  if (!event.treatAsNeverHappened || !event.voidCosts || !event.voidHours) return desk;
  const mission = desk.missions.find((row) => row.id === event.missionId);
  if (!mission) return desk;
  const log = desk.flightLogs.find((row) => row.tripId === mission.id);
  const posted =
    mission.postedHours ??
    (mission.hoursApplied
      ? {
          airframe: hoursFromTrip(mission, log),
          engine: hoursFromTrip(mission, log),
          apu: 0,
          cycle: true,
        }
      : null);
  const snap = careerSnapshot(desk, mission, log);
  const hoursStillPosted = Boolean(mission.postedHours) || (Boolean(mission.hoursApplied) && !snap);
  const fleet = snap
    ? desk.fleet.map((spec) => (tailMatches(spec, mission) ? restoreTail(spec, snap) : spec))
    : hoursStillPosted && posted
      ? desk.fleet.map((spec) => (tailMatches(spec, mission) ? reverseHours(spec, posted) : spec))
      : desk.fleet;
  const restored = fleet.find((spec) => tailMatches(spec, mission));
  const crashBasis = snap ?? (hoursStillPosted && restored ? careerBeforeFromTail(restored) : mission.crashBasis);
  const duty = desk.duty && log ? reverseFlightDuty(desk.duty, log, flightMinutesForDuty(log, mission)) : desk.duty;
  const squawkIds = new Set(log?.arrival?.squawkIds ?? []);
  const squawks = desk.squawks && squawkIds.size ? desk.squawks.filter((row) => !squawkIds.has(row.id)) : desk.squawks;
  const flightLogs = desk.flightLogs.map((row) => {
    if (row.tripId !== mission.id) return row;
    const arrival = row.arrival
      ? {
          ...row.arrival,
          engineHours: undefined,
          fuelUsedAmount: undefined,
          fuelUsedGal: undefined,
          squawkIds: undefined,
        }
      : undefined;
    return {
      ...row,
      status: row.status === "complete" || row.status === "arrived" || row.status === "airborne" ? "filed" : row.status,
      departedAtUtc: undefined,
      departedAtLocal: undefined,
      arrivedAtUtc: undefined,
      arrivedAtLocal: undefined,
      actualDepartureUtc: undefined,
      arrival,
    };
  });
  const logbook = log ? desk.logbook.filter((entry) => entry.flightLogId !== log.id) : desk.logbook;
  const voidedTxnIds = new Set(mission.crashVoidedTxnIds ?? []);
  const mine = desk.account.transactions.filter((txn) => txn.missionId === mission.id);
  const freshTxns = mine.filter((txn) => !voidedTxnIds.has(txn.id));
  const crashVoidedTxnIds = [...voidedTxnIds, ...freshTxns.map((txn) => txn.id)];
  const account = freshTxns.length
    ? {
        ...desk.account,
        balanceCad: Math.round((desk.account.balanceCad - freshTxns.reduce((sum, txn) => sum + txn.amountCad, 0)) * 100) / 100,
        transactions: desk.account.transactions.filter((txn) => txn.missionId !== mission.id),
      }
    : mine.length
      ? { ...desk.account, transactions: desk.account.transactions.filter((txn) => txn.missionId !== mission.id) }
      : desk.account;
  const voidedCrewIds = new Set(mission.crashVoidedCrewIds ?? []);
  const droppedCrew = event.keepCrewPay ? [] : desk.crewLedger.filter((row) => row.missionId === mission.id);
  const crashVoidedCrewIds = event.keepCrewPay
    ? mission.crashVoidedCrewIds
    : [...voidedCrewIds, ...droppedCrew.map((row) => row.id).filter((id) => !voidedCrewIds.has(id))];
  const crewLedger = event.keepCrewPay
    ? desk.crewLedger
    : desk.crewLedger.filter((row) => row.missionId !== mission.id);
  const tailTelemetry = desk.tailTelemetry
    ? stripFlightTelemetry(desk.tailTelemetry, mission.id, log?.id)
    : desk.tailTelemetry;
  const missions = desk.missions.map((row) => {
    if (row.id !== mission.id) return row;
    return {
      ...row,
      status: "pending" as const,
      lifecycle: "scheduled" as const,
      crashed: true,
      crashBasis,
      crashVoidedTxnIds,
      crashVoidedCrewIds,
      hoursApplied: false,
      ledgerPosted: false,
      postedHours: undefined,
      startedAt: undefined,
      arrivedAtUtc: undefined,
      completedAt: undefined,
      actualDepartureUtc: undefined,
      actualDepartureSource: undefined,
      dispatchWarning: replaceMarked(row.dispatchWarning, "CRASH", CRASH_NOTE),
    };
  });
  return {
    missions,
    costLedger: desk.costLedger.filter((line) => line.missionId !== mission.id),
    account,
    fleet,
    flightLogs,
    logbook,
    crewLedger,
    duty,
    tailTelemetry,
    squawks,
  };
}

export function applyFlightRestart(mission: Mission, event: FlightRestartEvent): Mission {
  const byKey = new Map(event.times.map((row) => [row.key, row.iso]));
  const departureIso = byKey.get("departure") ?? mission.departureIso;
  const legs = mission.legs?.map((leg) => {
    const iso = byKey.get(`leg:${leg.seq}`);
    return iso ? { ...leg, departureIso: iso } : leg;
  });
  return {
    ...mission,
    crashed: false,
    crashBasis: undefined,
    departureIso,
    legs: legs ?? mission.legs,
    dispatchWarning: replaceMarked(
      replaceMarked(mission.dispatchWarning, "CRASH", ""),
      "RESTART",
      `RESTART  first time is ${event.anchorIso}, 30 minutes past the clock.`,
    ),
  };
}

export function restartTimesOf(mission: Mission): { key: string; iso: string }[] {
  const times = [{ key: "departure", iso: mission.departureIso }];
  for (const leg of mission.legs ?? []) {
    if (leg.departureIso) times.push({ key: `leg:${leg.seq}`, iso: leg.departureIso });
  }
  return times;
}
